import { useEffect, useRef, type CSSProperties } from "react";
import { CountryFlag, countryName } from "@nexus/ui";
import { motionClass, prefersReducedMotion } from "@nexus/widget-kit";
import type { IpBlockingIsp } from "@nexus/widget-kit";
import { count, percent, rateColor } from "./rates";
import { useListFit } from "./useListFit";

/** Loop pace, in pixels per second: slow enough to read a row as it passes. */
const LOOP_SPEED = 24;

/**
 * Every measured ISP with its rate, in columns filled top to bottom. When its host
 * bounds the height and the rows still do not fit, they tighten, then loop slowly
 * (paused on hover or focus; scrolled by hand with reduced motion or while editing).
 */
export function AllIspList({
  entries,
  motion,
  showFlags,
}: {
  entries: IpBlockingIsp[];
  motion: boolean;
  showFlags: boolean;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLOListElement>(null);
  const animationRef = useRef<Animation | null>(null);
  const fit = useListFit(boxRef, entries.length);
  const loop = fit.mode === "loop";
  const animated = loop && motion && !prefersReducedMotion();
  // The loop restarts only when the rows change, not on every render of the widget.
  const rowsKey = entries.map((item) => `${item.isp}:${item.ratio}`).join("|");

  useEffect(() => {
    const track = trackRef.current;
    const list = listRef.current;
    if (!animated || !track || !list || typeof track.animate !== "function") return;
    const distance = list.offsetHeight + (parseFloat(getComputedStyle(track).rowGap) || 0);
    const animation = track.animate(
      [{ transform: "translateY(0)" }, { transform: `translateY(-${distance}px)` }],
      { duration: (distance / LOOP_SPEED) * 1000, iterations: Infinity },
    );
    animationRef.current = animation;
    return () => {
      animation.cancel();
      animationRef.current = null;
    };
  }, [animated, rowsKey, fit.columns]);

  if (!entries.length)
    return <p className="py-2 text-metric-detail text-muted">No eligible ISP sendings</p>;

  const rows = Math.ceil(entries.length / fit.columns);
  const list = (copy: boolean) => (
    <ol
      ref={copy ? undefined : listRef}
      aria-label={copy ? undefined : "ISP blocking rates"}
      aria-hidden={copy || undefined}
      className={`grid min-w-0 grid-flow-col gap-x-6 ${loop ? "shrink-0" : "flex-1 content-between"}`}
      style={{
        gridTemplateColumns: `repeat(${fit.columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, auto)`,
      }}
    >
      {entries.map((item, index) => {
        const country = countryName(item.country);
        const counts = `${count.format(item.blockedSendings)} / ${count.format(item.eligibleSendings)} approved ticket/ISP sendings confirmed blocked`;
        return (
          <li
            key={item.isp}
            className={`${copy ? "" : motionClass(motion, "nx-rise")} flex min-w-0 items-center gap-x-2 border-b border-border ${fit.mode === "normal" ? "py-1.5" : "py-0.5"}`}
            style={{ "--nx-delay": `${105 + Math.min(index, 20) * 30}ms` } as CSSProperties}
            title={`${item.isp} (${country ?? "country unavailable"}): ${counts}`}
          >
            {showFlags && <CountryFlag country={item.country} className="h-3.5 w-[1.1875rem]" />}
            <span className="min-w-0 flex-1 truncate text-metric-label font-medium text-primary">
              {item.isp}
            </span>
            <span
              className={`text-right text-metric-label font-semibold tabular-nums ${rateColor(item.ratio)}`}
            >
              {percent(item.ratio)}
            </span>
            {copy ? null : (
              <span className="sr-only">
                {country ? `${country}. ` : "Country unavailable. "}
                {counts}
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );

  return (
    <div
      ref={boxRef}
      className={`flex min-h-0 min-w-0 flex-1 flex-col ${
        animated
          ? "overflow-hidden [mask-image:linear-gradient(to_bottom,transparent,black_1rem,black_calc(100%-1rem),transparent)]"
          : loop
            ? "overflow-y-auto"
            : ""
      }`}
      onMouseEnter={() => animationRef.current?.pause()}
      onMouseLeave={() => animationRef.current?.play()}
      onFocus={() => animationRef.current?.pause()}
      onBlur={() => animationRef.current?.play()}
    >
      <div ref={trackRef} className={`flex flex-col gap-4 ${loop ? "" : "flex-1"}`}>
        {list(false)}
        {animated && list(true)}
      </div>
    </div>
  );
}
